/**
 * Phase 336 wave 3 task 3.1 (#1163) — the client's Sales Activity page, to the design
 * Alisha approved.
 *
 * <h3>What this file is, and what it deliberately is not</h3>
 * <p>It is a RENDERER. Every number on the page, and every rule about what a number means,
 * comes from `../reports/salesActivityModel.js`: the locked metric vocabulary, the month
 * bands (the ISO Thursday rule), which weeks are gaps, the year-to-date sums, the nine trend
 * series and each week's consultant split. Nothing is re-derived here — a second copy of
 * "which weeks exist" is exactly how a table and a chart start disagreeing about a week that
 * has no data, and that disagreement is invisible until a client points at it.</p>
 *
 * <p>What IS this file's own: the composition (tiles → trends → table), the expand/collapse
 * interaction and where its state lives, and the four states a read surface has to have
 * (loading, error, empty, and the real thing).</p>
 *
 * <h3>Why every week expands, not just the latest</h3>
 * <p>The pinned ask (card #1163, Alisha via Carlos): "Can we expand and collapse the weeks
 * with it listing all the previous weeks?" — so the toggle is a property of a WEEK ROW, not
 * of the first row. A week is expandable exactly when the fact store recorded a consultant
 * split for it, which is true for every historical week the engine has written, and honestly
 * false for a gap week (a disclosure that opens onto nothing is worse than no disclosure).</p>
 *
 * <h3>Why the open weeks live in sessionStorage</h3>
 * <p>A client opens three weeks, clicks through to the other report, comes back — and the
 * page they left is the page they return to. `sessionStorage`, not `localStorage`, is the
 * card's own boundary: "survives navigation within the browser session". Weeks that were
 * interesting on Monday are stale by the following Monday, so state that outlived the tab
 * would be re-opening last month's rows at someone who has moved on. The key is per TENANT,
 * so a shared machine can never show one client's opened rows under another's numbers.</p>
 *
 * <h3>Freshness</h3>
 * <p>The "Data as at" line is the FACT STORE's `as_of` (ADR-074 D4 / ADR-068 D3) — when the
 * numbers were computed, never when this browser fetched them. The roster read carries its
 * own, later, as-of (it is a live name lookup); using that one here would tell a client their
 * figures were fresher than they are.</p>
 *
 * <p>Design tokens only — no raw hex (test/polish/hex-fence.test.ts).</p>
 */
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { EmptyState, PageHeader, TrendSmallMultiple } from "@ardept/design-system";
import { useClientTenant, useRoster, useWeeklyActivity } from "../api/portalQueries.js";
import { SALES_ACTIVITY_LABEL } from "../nav/familyRegistry.js";
import { usePageTitle } from "../nav/usePageTitle.js";
import type { WeeklyActivityCell } from "../api/types.js";
import {
  METRIC_LEGEND_LINE,
  PARTIAL_DATA_LABEL,
  SALES_ACTIVITY_METRICS,
  currentIsoYear,
  trendSeries,
  weekRows,
  weekSplit,
  ytdTiles,
  type MetricValue,
  type SplitRow,
} from "../reports/salesActivityModel.js";
import "./SalesActivityPage.css";

// ── Expand state, on the browser's session ──────────────────────────────────────────────

/**
 * The sessionStorage key prefix for the set of opened weeks.
 *
 * <p>Namespaced (`ard-portal:`) because sessionStorage is per ORIGIN, and on a shared origin
 * an un-namespaced `expanded` would be a collision waiting for the second app. Suffixed with
 * the tenant id for the same reason at a smaller scale: one browser, two client sign-ins,
 * two different sets of interesting weeks.</p>
 */
export const EXPANDED_KEY_PREFIX = "ard-portal:sales-activity:expanded:";

/** The sessionStorage key holding this tenant's opened weeks. */
export function expandedStorageKey(tenantId: string): string {
  return `${EXPANDED_KEY_PREFIX}${tenantId}`;
}

/**
 * Read the opened weeks for a tenant.
 *
 * <p>Every failure mode — storage disabled by policy, a half-written value, someone else's
 * JSON under our key — degrades to "nothing is open". A report that will not render because
 * a UI preference could not be parsed is a far worse outcome than a report whose rows all
 * start closed, so this never throws and never propagates.</p>
 */
function readExpanded(tenantId: string | null): Set<string> {
  if (!tenantId) return new Set();
  try {
    const raw = window.sessionStorage.getItem(expandedStorageKey(tenantId));
    if (!raw) return new Set();
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((week): week is string => typeof week === "string"));
  } catch {
    return new Set();
  }
}

/** Persist the opened weeks. Storage being unavailable costs persistence, never the page. */
function writeExpanded(tenantId: string | null, weeks: ReadonlySet<string>): void {
  if (!tenantId) return;
  try {
    window.sessionStorage.setItem(expandedStorageKey(tenantId), JSON.stringify([...weeks]));
  } catch {
    /* Private mode, a full quota, a storage-blocking policy: the page works, the memory does not. */
  }
}

// ── Small helpers ───────────────────────────────────────────────────────────────────────

/** Stable identity for "no cells", so the memos below do not recompute on every render. */
const NO_CELLS: readonly WeeklyActivityCell[] = [];

/** The fact store's `as_of`, as a date a person reads rather than an ISO timestamp. */
function formatAsOf(asOf: string | null | undefined): string {
  if (!asOf) return "";
  const when = new Date(asOf);
  if (Number.isNaN(when.getTime())) return "";
  return when.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
}

/**
 * Phase 380 wave 3 task 3.3 (#1329) — the disclosure a week whose Loxo harvest ran short
 * wears: every number on that row is a FLOOR, not a final figure.
 *
 * <p><b>The words, visibly, on the row itself.</b> Not a `title` tooltip and not a bare glyph:
 * a tooltip is unreachable by keyboard and invisible on touch, and a lone ⚠ is a symbol the
 * client has to be taught. This is the same rule the split rows already follow — the support
 * consultant is dimmed AND says "support", "for anyone the dimming does not reach". The mark
 * is therefore decorative (`aria-hidden`) and the sentence carries the meaning, so colour,
 * glyph and words all three say it and no single channel is load-bearing.</p>
 *
 * <p><b>Beside the week label, not inside the toggle.</b> The disclosure button's accessible
 * name is `{week} consultant breakdown` and WCAG 2.5.3 wants its visible text contained in
 * that name; folding this sentence into the button would break that, and would also make a
 * factual disclosure look like part of a control. It sits in the row header next to the week
 * it qualifies, which is where a reader's eye already is when they ask "which week?".</p>
 *
 * <p>Rendered from {@link PARTIAL_DATA_LABEL} — the model's copy of the Monday card's sentence
 * — never from a literal typed here.</p>
 */
function PartialDataBadge() {
  return (
    <span className="sa-partial">
      <span className="sa-partial__mark" aria-hidden="true">
        ⚠
      </span>
      <span className="sa-partial__text">{PARTIAL_DATA_LABEL}</span>
    </span>
  );
}

/**
 * The nine value cells of a row, exactly as the model formatted them — the dash for a zero
 * and the grouped number for anything else are ITS rules, applied identically to a week
 * total and to a consultant's line inside that week.
 */
function ValueCells({ values }: { values: readonly MetricValue[] }) {
  return (
    <>
      {values.map((value) => (
        <td key={value.key} className={`num${value.isZero ? " is-zero" : ""}`}>
          {value.display}
        </td>
      ))}
    </>
  );
}

// ── The page ────────────────────────────────────────────────────────────────────────────

export function SalesActivityPage(): JSX.Element {
  const identity = useClientTenant();
  const tenantId = identity.tenantId;

  // The tab says what the nav rail says, because both read the one label (#1163 AC-4).
  usePageTitle(SALES_ACTIVITY_LABEL);

  const weekly = useWeeklyActivity(tenantId);
  const roster = useRoster(tenantId);
  const data = weekly.data;

  // Today, fixed for the life of this mount. The model takes `now` as a parameter precisely
  // so it can be tested at a year boundary; reading the clock is this layer's job, and doing
  // it once means the tiles cannot change their mind mid-render.
  const now = useMemo(() => new Date(), []);

  const cells = data?.cells ?? NO_CELLS;

  /**
   * The roster is a LABELLING read, never a gating one. An unreachable roster answers with an
   * empty list (its documented degradation) and the splits fall back to `User {id}`; even an
   * outright failure must not turn a page full of real numbers into an error, so `roster.error`
   * is deliberately absent from `failed` below.
   */
  const rosterRows = roster.data?.consultants ?? null;

  // "Still loading" is defined by the ANSWER, not by a fetch flag — the query is disabled
  // until /me yields a tenant, and a disabled query reports isLoading:false, which would
  // flash the empty state at a client whose reports are on their way.
  const gridUnresolved = tenantId !== null && data === undefined && weekly.error === null;
  const isBusy = identity.isLoading || gridUnresolved;
  const failed = identity.error ?? weekly.error ?? null;
  const isEmpty = !!data && data.cells.length === 0;

  // ── The page model, all of it from the selectors ──
  const tiles = useMemo(() => ytdTiles(cells, now), [cells, now]);
  const trends = useMemo(() => trendSeries(cells), [cells]);
  const rows = useMemo(() => weekRows(cells), [cells]);

  /**
   * The weeks the fact store recorded a consultant split for — i.e. the weeks a toggle can
   * honestly open. Derived from the cells rather than assumed from `hasData`, so a week the
   * engine wrote a split but no tenant total for still offers its split instead of hiding it.
   */
  const weeksWithSplit = useMemo(() => {
    const weeks = new Set<string>();
    for (const cell of cells) if (cell.scope === "per_consultant") weeks.add(cell.iso_week);
    return weeks;
  }, [cells]);

  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set<string>());
  const restoredFor = useRef<string | null>(null);

  // Restore ONCE per tenant, when /me finally names one. Re-running on every render would
  // fight the user's clicks; re-running per tenant is what makes the key per-tenant mean
  // anything at all.
  useEffect(() => {
    if (!tenantId || restoredFor.current === tenantId) return;
    restoredFor.current = tenantId;
    setExpanded(readExpanded(tenantId));
  }, [tenantId]);

  function toggleWeek(isoWeek: string): void {
    const next = new Set(expanded);
    if (next.has(isoWeek)) next.delete(isoWeek);
    else next.add(isoWeek);
    setExpanded(next);
    // Written here rather than in an effect on `expanded`: an effect would also fire for the
    // initial empty set and overwrite the stored weeks before the restore above had read them.
    writeExpanded(tenantId, next);
  }

  /** The split rows for the OPEN weeks only — a closed week costs nothing to keep closed. */
  const splitsByWeek = useMemo(() => {
    const byWeek = new Map<string, SplitRow[]>();
    for (const isoWeek of expanded) {
      if (!weeksWithSplit.has(isoWeek)) continue;
      byWeek.set(isoWeek, weekSplit(cells, rosterRows, isoWeek));
    }
    return byWeek;
  }, [cells, rosterRows, expanded, weeksWithSplit]);

  const metricColumns = SALES_ACTIVITY_METRICS.length;
  const asOf = formatAsOf(data?.as_of);

  return (
    <>
      <PageHeader
        title={SALES_ACTIVITY_LABEL}
        subtitle="Your team's activity, week by week. Most recent week first — open a week to see who did what."
        actions={
          data && !isEmpty && asOf ? (
            <p className="page-header__meta">
              Data as at <span className="num">{asOf}</span>
            </p>
          ) : undefined
        }
      />

      {failed && (
        <div className="state state--error" role="status">
          <h2 className="state__title">We could not load this report</h2>
          <p className="state__body">
            Please try again in a moment. If it keeps happening, contact your AR Dept contact.
          </p>
        </div>
      )}

      {!failed && isBusy && (
        <div className="sa-loading" aria-busy="true" aria-live="polite">
          <span className="sa-loading__label">Loading your sales activity…</span>
          <div className="skeleton" style={{ width: "35%" }} />
          <div className="skeleton" style={{ width: "80%" }} />
          <div className="skeleton" />
          <div className="skeleton" />
        </div>
      )}

      {!failed && !isBusy && isEmpty && (
        <EmptyState
          iconName="bar-chart-2"
          title="No activity yet"
          body="Your sales activity will appear here as soon as your first report has run."
        />
      )}

      {!failed && !isBusy && !isEmpty && data && (
        <>
          {/* ── Year to date: the nine headline numbers ── */}
          <section className="sa-section" aria-labelledby="sa-ytd-head">
            <h2 className="sa-section__head" id="sa-ytd-head">
              Year to date
            </h2>
            <p className="sa-section__note">
              Every week recorded so far in <span className="num">{currentIsoYear(now)}</span>,
              across the whole team.
            </p>
            <div className="summary">
              {tiles.map((tile) => (
                <div className="summary__item" key={tile.key}>
                  <p className="summary__k">{tile.label}</p>
                  <p className={`summary__v${tile.isZero ? " is-zero" : ""}`}>{tile.display}</p>
                </div>
              ))}
            </div>
          </section>

          {/* ── Trends: nine small multiples, oldest → newest ── */}
          {trends.length > 0 && (
            <section className="sa-section" aria-labelledby="sa-trends-head">
              <h2 className="sa-section__head" id="sa-trends-head">
                Trends
              </h2>
              <p className="sa-section__note">
                Each measure across every recorded week, earliest on the left. A break in a
                line is a week with no data recorded — never a zero. Notes counts every note
                logged, so it sits on its own scale and is marked as such.
              </p>
              <div className="sa-trends">
                {trends.map((series) => (
                  <figure className="sa-trend" key={series.key}>
                    <figcaption className="sa-trend__title">{series.label}</figcaption>
                    <TrendSmallMultiple
                      points={series.points}
                      labels={series.labels}
                      yMax={series.yMax}
                      ownScale={series.ownScale}
                    />
                  </figure>
                ))}
              </div>
            </section>
          )}

          {/* ── By week: month-banded, newest first, every row an expandable split ── */}
          {rows.length > 0 && (
            <section className="sa-section" aria-labelledby="sa-weeks-head">
              <h2 className="sa-section__head" id="sa-weeks-head">
                By week
              </h2>
              <p className="sa-section__note">
                Most recent week first, grouped by month. Open any week — this one or one from
                months ago — to see the same numbers split by consultant.
              </p>
              <div className="tbl-wrap">
                <table className="tbl sa-weeks" aria-label="Sales activity by week">
                  <thead>
                    <tr>
                      <th scope="col">Week</th>
                      {SALES_ACTIVITY_METRICS.map((metric) => (
                        <th scope="col" className="num" key={metric.key}>
                          <abbr title={metric.label}>{metric.abbrev}</abbr>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => {
                      const isOpen = expanded.has(row.isoWeek);
                      const canExpand = weeksWithSplit.has(row.isoWeek);
                      const splits = splitsByWeek.get(row.isoWeek) ?? [];
                      return (
                        <Fragment key={row.isoWeek}>
                          {row.startsMonthBand && (
                            <tr className="sa-band">
                              <th scope="colgroup" colSpan={metricColumns + 1}>
                                {row.monthLabel}
                              </th>
                            </tr>
                          )}

                          <tr
                            className={`sa-row sa-row--week${isOpen ? " is-open" : ""}${
                              row.hasData ? "" : " sa-row--gap"
                            }`}
                            data-week={row.isoWeek}
                          >
                            <th scope="row">
                              {canExpand ? (
                                <button
                                  type="button"
                                  className="sa-toggle"
                                  aria-expanded={isOpen}
                                  aria-label={`${row.isoWeek} consultant breakdown`}
                                  onClick={() => toggleWeek(row.isoWeek)}
                                >
                                  <span className="sa-toggle__chev" aria-hidden="true" />
                                  <span className="sa-toggle__week">{row.isoWeek}</span>
                                </button>
                              ) : (
                                <span className="sa-week">{row.isoWeek}</span>
                              )}
                              {/* Per WEEK, off the model's own verdict — the page never
                                  re-derives which harvest was short, and a clean week beside
                                  a short one stays clean. */}
                              {row.activityHarvestIncomplete && <PartialDataBadge />}
                            </th>

                            {row.hasData ? (
                              <ValueCells values={row.values} />
                            ) : (
                              /* A gap is absence, said out loud — never a row of zeros, and
                                 never a silently missing week between two that are there. */
                              <td className="sa-gap" colSpan={metricColumns}>
                                {row.gapLabel}
                              </td>
                            )}
                          </tr>

                          {isOpen &&
                            (splits.length > 0 ? (
                              splits.map((split) => (
                                <tr
                                  className={`sa-row sa-row--split${split.dimmed ? " is-dimmed" : ""}`}
                                  key={`${row.isoWeek}::${split.loxoUserId}`}
                                  data-week={row.isoWeek}
                                  data-consultant={split.loxoUserId}
                                >
                                  <th scope="row" className="sa-who">
                                    <span className="sa-who__name">{split.name}</span>
                                    {/* Dimming alone would carry this on colour only. The word
                                        says it too, for anyone the dimming does not reach. */}
                                    {split.dimmed && <span className="sa-who__tag">support</span>}
                                  </th>
                                  <ValueCells values={split.values} />
                                </tr>
                              ))
                            ) : (
                              /* Reachable when the engine wrote this week's split under a
                                 metric key the locked vocabulary does not know (a tenth
                                 metric): the cells exist, so the toggle is offered, but the
                                 model renders none of them. Saying so beats opening onto an
                                 empty space that reads as a broken page. */
                              <tr className="sa-row sa-row--split" data-week={row.isoWeek}>
                                <td className="sa-gap" colSpan={metricColumns + 1}>
                                  No consultant breakdown recorded for this week.
                                </td>
                              </tr>
                            ))}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <p className="sa-legend">{METRIC_LEGEND_LINE}</p>
            </section>
          )}
        </>
      )}
    </>
  );
}

export default SalesActivityPage;
